/**
 * What the file viewer can show for a content type.
 *
 * image/pdf keep their existing lightbox/document viewer; the rest open in
 * FileViewer. Office documents get no conversion — just a card with
 * Download / Open — except xlsx, whose first sheet the server returns as rows.
 */
export type PreviewKind = "image" | "pdf" | "video" | "audio" | "text" | "csv" | "xlsx" | "office";

const OFFICE = new Set([
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
]);

export function previewKindFor(contentType: string): PreviewKind | null {
  if (["image/png", "image/jpeg", "image/gif", "image/webp"].includes(contentType)) return "image";
  if (contentType === "application/pdf") return "pdf";
  if (["video/mp4", "video/webm", "video/quicktime"].includes(contentType)) return "video";
  if (["audio/mpeg", "audio/mp4", "audio/ogg", "audio/wav"].includes(contentType)) return "audio";
  if (contentType === "text/csv") return "csv";
  if (["text/plain", "text/markdown", "application/json"].includes(contentType)) return "text";
  if (contentType === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") return "xlsx";
  if (OFFICE.has(contentType)) return "office";
  return null;
}

/** Kinds the generic FileViewer handles (image and pdf have their own). */
export function opensInFileViewer(contentType: string): boolean {
  const kind = previewKindFor(contentType);
  return kind !== null && kind !== "image" && kind !== "pdf";
}

/** Minimal RFC 4180 parser: quoted fields, doubled quotes, CRLF or LF. */
export function parseCsv(text: string, maxRows = 500): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;
  for (let i = 0; i < text.length && rows.length < maxRows; i += 1) {
    const ch = text[i]!;
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') {
        field += '"';
        i += 1;
      } else if (ch === '"') quoted = false;
      else field += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ",") {
      row.push(field);
      field = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i += 1;
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else field += ch;
  }
  if ((field !== "" || row.length > 0) && rows.length < maxRows) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}
